import { useTranslation } from 'react-i18next';
import Button from '../ui/Button';
import { ANSWERS, unanswered } from '../../utils/ai/fieldTraits';

/*
 * Asking a person to look at the base or the stem, L16 (utils/ai/fieldTraits.js says when, and why nothing here can
 * clear anything). The wording is docs/LIVE_UX_PLAN.md 3.3: a question with "Can't tell", never a finding.
 *
 *   FieldTraitChecks  in the live AI panel, under the matches and the safety banner.
 *   FieldTraitLook    over the live camera once the panel is closed, one question at a time, so the person can go and
 *                     look with the camera in hand. Non-modal, and above the shutter.
 *   FieldTraitGuide   over the frozen frame when the circle reaches the bottom: a band on the screen's edge. It's fixed
 *                     to the screen, not to the mushroom, because nothing here knows where a part is.
 */
function TraitAsk({ ask, answer, onAnswer, mayRecord, held, testId, t }) {
  const questionId = `${testId}-${ask.trait}-question`;
  return (
    <div data-testid={`${testId}-${ask.trait}`} data-why={ask.why} data-answer={answer || undefined} style={st.ask}>
      <strong id={questionId} style={st.question}>{t(`traits.asks.${ask.trait}.ask`)}</strong>
      <span style={st.small}>{t(`traits.asks.${ask.trait}.hint`)}</span>
      <span style={st.small} data-testid={`${testId}-${ask.trait}-why`}>
        {t(`traits.why.${ask.why}`, ask.why === 'warning' ? undefined : { taxa: ask.taxa.join(', ') })}
      </span>
      {mayRecord ? (
        <div role="group" aria-labelledby={questionId} style={st.answers}>
          {ANSWERS.map(option => (
            <Button key={option} variant="secondary" aria-pressed={answer === option}
              style={answer === option ? st.chosen : undefined}
              onClick={() => onAnswer(ask.trait, option)} data-testid={`${testId}-${ask.trait}-${option}`}>
              {t(`traits.answer.${option}`)}
            </Button>
          ))}
        </div>
      ) : (
        <span style={st.small} data-testid={`${testId}-${ask.trait}-not-recorded`}>{t('traits.notRecorded')}</span>
      )}
      {answer === 'no' && (
        <span role="note" style={st.note} data-testid={`${testId}-${ask.trait}-clears`}>{t('traits.noClears')}</span>
      )}
      {answer && <span style={st.small}>{t(held ? 'traits.held' : 'traits.kept')}</span>}
    </div>
  );
}

export default function FieldTraitChecks({ asks, answers, onAnswer, warned = false, framing = false, mayRecord = false,
  held = false, testId = 'field-traits' }) {
  const { t } = useTranslation('ai');
  if (!asks.length) return null;
  return (
    <section data-testid={testId} aria-labelledby={`${testId}-title`} style={st.block}>
      <h3 id={`${testId}-title`} style={st.title}>{t('traits.title')}</h3>
      <p style={st.small}>{t('traits.askOnly')}</p>
      {!warned && <p style={st.small} data-testid={`${testId}-no-warning`}>{t('traits.noWarning')}</p>}
      {framing && asks.some(ask => ask.trait === 'volva') && (
        <p style={st.note} data-testid={`${testId}-framing`}>{t('traits.framing')}</p>
      )}
      {asks.map(ask => (
        <TraitAsk key={ask.trait} ask={ask} answer={answers?.[ask.trait]} onAnswer={onAnswer} mayRecord={mayRecord}
          held={held} testId={testId} t={t} />
      ))}
    </section>
  );
}

export function FieldTraitLook({ asks, answers, onAnswer, onDismiss, onPhotographBase, warned = false, mayRecord = false,
  held = false, offset = 0, testId = 'field-traits-look' }) {
  const { t } = useTranslation('ai');
  const [ask] = unanswered(asks, answers);
  if (!ask) return null;
  return (
    <section data-testid={testId} aria-label={t('traits.lookLabel')} style={{ ...st.look, bottom: `${offset + 8}px` }}>
      {warned && <p role="note" style={st.warning} data-testid={`${testId}-dangerous`}>{t('live.dangerous')}</p>}
      <TraitAsk ask={ask} onAnswer={onAnswer} mayRecord={mayRecord} held={held} testId={testId} t={t} />
      <div style={st.answers}>
        {ask.trait === 'volva' && onPhotographBase && (
          <Button variant="secondary" onClick={onPhotographBase} data-testid={`${testId}-photograph`}>
            {t('traits.photographBase')}
          </Button>
        )}
        <Button variant="ghost" onClick={onDismiss} data-testid={`${testId}-dismiss`}>{t('traits.notNow')}</Button>
      </div>
    </section>
  );
}

export function FieldTraitGuide({ offset = 0, testId = 'field-traits-guide' }) {
  const { t } = useTranslation('ai');
  return (
    <div data-testid={testId} aria-hidden="true" style={{ ...st.guide, bottom: `${offset}px` }}>
      <span style={st.guideWords}>{t('traits.guide')}</span>
    </div>
  );
}

const st = {
  block: { margin: '10px 0 0', paddingTop: 8, borderTop: '1px solid var(--c-border-strong)' },
  title: { margin: '0 0 4px', fontSize: 14 },
  ask: { display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 },
  question: { fontSize: 14 },
  small: { margin: 0, fontSize: 12, color: 'var(--c-text-muted-fg)' },
  note: { margin: '4px 0 0', fontSize: 13, fontWeight: 600 },
  warning: { margin: 0, fontSize: 13, fontWeight: 600, color: 'var(--c-danger-fg)' },
  answers: { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 },
  chosen: { outline: '2px solid currentColor', outlineOffset: 1, fontWeight: 700 },
  look: {
    position: 'absolute', zIndex: 3, insetInlineStart: 12, insetInlineEnd: 12, margin: '0 auto', maxWidth: 520,
    maxHeight: 'min(44dvh, 360px)', overflowY: 'auto', boxSizing: 'border-box', padding: '10px 12px 12px',
    borderRadius: 14, background: 'var(--c-surface)', color: 'var(--c-text-body-fg)',
    boxShadow: '0 8px 28px rgba(0, 0, 0, 0.45)', border: '1px solid var(--c-border-strong)',
  },
  guide: {
    position: 'absolute', zIndex: 2, insetInlineStart: 0, insetInlineEnd: 0, height: 44, pointerEvents: 'none',
    display: 'flex', alignItems: 'center', justifyContent: 'center', borderTop: '2px dashed #fff',
    background: 'rgba(0, 0, 0, 0.45)',
  },
  guideWords: { color: '#fff', fontSize: 13, fontWeight: 600 },
};
